import { createSelector } from "reselect"
import { get, chain, map, filter, first, find, mergeWith, some, isEmpty, size, omit } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { positionSetContributorsSummaryText, sortContributors, mapPerformanceContributorsReturnsToValues, mapPerformanceFactorsReturnsToValue } from "helpers/contributors"
import { getAllAvailableRollups } from "helpers/rollups"
import { createQueryStringSelector } from "selectors/queryString"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { computeFactorCategories } from "helpers/factors"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { makeGroupSort } from "helpers/contributorsSort"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

export const getGroupBy = (_, props) => get(props, "currentGrouping.id")
export const getSelectedContributorCategory = (_, props) => get(props, "selectedContributorCategory", EMPTY_OBJECT)
const getViewType = (_, props) => props.viewType
const getSelectedViews = (_, props) => props.selectedViews
const getSelectedViewsFactors = (_, props) => props.selectedViewsFactors
const getClassifications = (_, props) => props.classifications
const getPositionSetName = (_, props) => props.positionSetName
const getRawContributorsData = state => get(state, "global.data.compositePerformanceContributors.data.model.simulation.performanceContributors")
const getSortOrder = (_, props) => get(props, "sortOrder", EMPTY_OBJECT)
const getRawGroupedContributorsData = state => get(state, "global.data.compositeGroupedPerformanceContributors.data.model.simulation.groupedPerformanceContributors")
const getCurrentGrouping = (_, props) => get(props, "currentGrouping")
const getSelectedGroupId = createQueryStringSelector("contributorGroup")
const getSelectedDate = (_, props) => props.selectedDate
const getShowNotHeld = (_, props) => props.showNotHeld
const getMustAggregateFactors = (_, props) => props.mustAggregateFactors
const getFactorsClassification = (_, props) => props.factorsClassification

function getFactors(factors, mustAggregateFactors, selectedViewsFactors, factorsClassification) {
  if (!mustAggregateFactors) {
    return factors
  }
  const result = aggregateFactorsSumBy(factorsClassification, factors, "value")

  if (isEmpty(selectedViewsFactors)) {
    return result
  }

  return chain(factors)
    .filter(factor => some(selectedViewsFactors, item => item.id === factor.id))
    .concat(result)
    .value()
}

function createFilter(selectedField) {
  // Display total if it contributes to factors, alpha or trading
  if (selectedField === "total") {
    return security => security.alpha || security.factorsTotal || security.trading
  } else {
    return security => get(security, selectedField)
  }
}

function mapFactors(factors) {
  return chain(factors)
    .keyBy("id")
    .mapValues(f => f.value)
    .value()
}

function computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) {
  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )
  return chain(contributors)
    .filter(contributor => contributor.notHeld)
    .reduce((notHeldContributorBucket, contributor) => {
      notHeldContributorBucket.size += contributor.averagePercentEquity
      notHeldContributorBucket.total += get(contributor, "totalReturn.cumulativePercent")
      notHeldContributorBucket.factorsTotal += get(contributor, "attribution.summary.factorReturn.cumulativePercent")
      notHeldContributorBucket.alpha += get(contributor, "attribution.summary.specificReturn.cumulativePercent")
      notHeldContributorBucket.trading += get(contributor, "attribution.summary.tradingReturn.cumulativePercent")

      const factors = getFactors(contributor.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)
      merge(notHeldContributorBucket.factors, mapFactors(factors))

      const factorCategories = computeFactorCategories(factors, selectedViews)
      merge(notHeldContributorBucket.categories, factorCategories)

      return notHeldContributorBucket
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      total: 0,
      factorsTotal: 0,
      alpha: 0,
      trading: 0,
      factors: {},
      categories: {},
    })
    .value()
}

function mapPerformanceContributors(contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) {
  const result = chain(contributors)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      const factors = getFactors(security.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)
      return {
        ...omit(security, ["attribution"]),
        total: get(security, "totalReturn.cumulativePercent"),
        size: security.averagePercentEquity,
        classification: get(security, "classification.name"),
        factorsTotal: get(security, "attribution.summary.factorsReturn.cumulativePercent"),
        alpha: get(security, "attribution.summary.specificReturn.cumulativePercent"),
        trading: get(security, "attribution.summary.tradingReturn.cumulativePercent"),
        factors: mapFactors(factors),
        categories: computeFactorCategories(factors, selectedViews),
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification))
  }
  return result
}

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

const getSelectedField = createSelector(
  [getSelectedContributorCategory],
  (selected) => get(selected, "field", "total"),
)

const getGroupedContributorsData = createSelector(
  [getRawGroupedContributorsData],
  (groupedContributors) => map(groupedContributors, group => ({
    ...group,
    attribution: {
      ...group.attribution,
      factors: mapPerformanceFactorsReturnsToValue(group.attribution.factors),
    },
    contributors: mapPerformanceContributorsReturnsToValues(group.contributors),
  })),
)

export const getSelectedGroup = createSelector(
  [getSelectedGroupId, getGroupedContributorsData],
  (selectedGroupId, groupedData) => computeSelectedGroup({ id: selectedGroupId }, groupedData, "averagePercentEquity"),
)

const getContributorsData = createSelector([getRawContributorsData], mapPerformanceContributorsReturnsToValues)

const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getFactorsClassification],
  mapPerformanceContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) => filter(data, createFilter(selectedField)),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getSortOrder],
  (data, selectedField, order) => {
    if (!size(data)) return []
    return sortContributors(data, selectedField, order)
  },
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getFactorsClassification],
  (groups, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) => map(groups, group => {
    const factors = getFactors(group.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)
    return {
      ...group,
      total: get(group, "totalReturn.cumulativePercent"),
      factorsTotal: get(group, "attribution.summary.factorsReturn.cumulativePercent"),
      alpha: get(group, "attribution.summary.specificReturn.cumulativePercent"),
      trading: get(group, "attribution.summary.tradingReturn.cumulativePercent"),
      factors: mapFactors(factors),
      categories: computeFactorCategories(factors, selectedViews),
      totalPercentEquity: group.averagePercentEquity,
      contributors: mapPerformanceContributors(group.contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification),
    }
  }),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => {
    const groupFilter = selectedGroup ? group => selectedGroup.id === group.id : createFilter(selectedField)
    return chain(data)
      .filter(groupFilter)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, createFilter(selectedField)),
      }))
      .value()
  },
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getSortOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField) * 100,
        contributors: sortContributors(group.contributors, selectedField, order),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

const getAccountRollups = createSelector(
  [getClassifications],
  (classifications) => ({
    classifications,
  }),
)

export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getCurrentGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelectedContributorCategory, getCurrentGrouping, getPositionSetName, getSelectedDate, getViewType, getSelectedGroup],
  (data, groupedData, selected, currentGrouping, positionSetName, selectedDate, viewType, selectedGroup) => {
    if (selectedGroup && size(groupedData)) {
      return computeGroupedContributorsCSV({
        type: "Performance",
        portfolioName: positionSetName,
        selected,
        selectedDate,
        viewType,
        group: first(groupedData),
      })
    }

    if (currentGrouping) {
      return computeContributorsGroupCSV({
        type: "Performance",
        portfolioName: positionSetName,
        selected,
        selectedDate,
        viewType,
        contributorsGrouping: currentGrouping,
        data: groupedData,
      })
    }

    return computeContributorsCSV({
      type: "Performance",
      portfolioName: positionSetName,
      selected,
      selectedDate,
      viewType,
      data,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField) * 100
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .filter(createFilter(selectedField))
        .map(group => ({
          ...group,
          value: get(group, selectedField) * 100,
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
